/**
 * Browser-side helpers for the landing-page Context Composer.
 *
 * These rules make attachment predictable and immediate. The server still
 * reclassifies every URL at the trusted boundary before persisting it.
 */

import { classifyInstagramBusinessUrl } from '@mouja/domain/merchant-intake';

export interface ContextSourceOptions {
  readonly instagramBusinessEnabled?: boolean;
}

export type ContextPlatform = 'website' | 'instagram' | 'tiktok';
export type ContextOwnership = 'mine' | 'similar' | null;
export type ContextKind = 'Instagram' | 'TikTok';
export type ContextIcon = 'globe' | 'camera' | 'music';

export interface ContextSource {
  /** Stable normalized de-duplication key. */
  readonly id: string;
  readonly platform: ContextPlatform;
  /** Kept for the waitlist helper and older presentation components. */
  readonly kind: ContextKind | null;
  readonly label: string;
  readonly icon: ContextIcon;
  /** Canonical form used for presentation, deduplication and legacy API requests. */
  readonly url: string;
  /** Validated, trimmed input retained only for the additive Instagram-only handoff. */
  readonly originalUrl?: string;
  /** Both accepted intake sources are first-party business evidence. */
  readonly ownership: ContextOwnership;
}

export type ContextValueInspection =
  | { readonly kind: 'text' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'unsupported' }
  | { readonly kind: 'source'; readonly source: ContextSource };

const WEBSITE_SOCIAL_HOSTS = [
  'instagram.com',
  'tiktok.com',
  'snapchat.com',
  'facebook.com',
  'x.com',
  'youtube.com',
  'youtu.be',
  'threads.net',
  'linkedin.com',
  'pinterest.com',
] as const;

const INSTAGRAM_RESERVED_PATHS = new Set([
  'about',
  'accounts',
  'developer',
  'directory',
  'explore',
  'p',
  'reel',
  'reels',
  'stories',
  'tv',
]);

function isInstagramProfilePath(parts: readonly string[]): boolean {
  const handle = parts[0] ?? '';
  return (
    parts.length === 1 &&
    !INSTAGRAM_RESERVED_PATHS.has(handle.toLowerCase()) &&
    /^[a-z0-9._]+$/iu.test(handle)
  );
}

function isTikTokProfilePath(parts: readonly string[]): boolean {
  return parts.length === 1 && /^@[a-z0-9._]+$/iu.test(parts[0] ?? '');
}

function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

function parsedContextUrl(raw: string): URL | null {
  const value = raw.trim();
  if (value.length === 0 || /\s/u.test(value)) return null;
  let url: URL;
  try {
    url = new URL(/^https?:\/\//iu.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^www\./u, '');
  if (!host.includes('.') || host.startsWith('.') || host.endsWith('.')) return null;
  return url;
}

function intendsToBeUrl(raw: string): boolean {
  const value = raw.trim();
  return (
    /^[a-z][a-z0-9+.-]*:/iu.test(value) ||
    /^www\./iu.test(value) ||
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?(?:[/?#]|$)/iu.test(
      value,
    )
  );
}

/** The exact composer normalization: lowercase and visually scheme-free. */
export function normalizeContextValue(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//u, '')
    .replace(/^www\./u, '')
    .replace(/\/$/u, '');
}

export function contextValueIsValid(raw: string, platform: ContextPlatform): boolean {
  return contextPlatformForValue(raw) === platform;
}

/** Identify a supported product or reference URL without requiring a mode switch. */
export function contextPlatformForValue(raw: string): ContextPlatform | null {
  const url = parsedContextUrl(raw);
  if (url === null) return null;
  const host = url.hostname.toLowerCase().replace(/^www\./u, '');
  const pathParts = url.pathname.split('/').filter(Boolean);
  if (hostMatches(host, 'instagram.com')) {
    return isInstagramProfilePath(pathParts) ? 'instagram' : null;
  }
  if (hostMatches(host, 'tiktok.com')) {
    return isTikTokProfilePath(pathParts) ? 'tiktok' : null;
  }
  return WEBSITE_SOCIAL_HOSTS.some((domain) => hostMatches(host, domain))
    ? null
    : 'website';
}

export function contextSourceFrom(
  raw: string,
  platform: ContextPlatform,
): ContextSource | null {
  if (!contextValueIsValid(raw, platform)) return null;
  const normalized = normalizeContextValue(raw);
  const path = normalized.split('/').slice(1);
  const socialHandle = path.find((segment) => segment.startsWith('@'));
  const firstSegment = path[0] ?? '';
  const label =
    platform === 'website'
      ? (normalized.split('/')[0] ?? normalized)
      : platform === 'tiktok'
        ? (socialHandle ?? '@profile')
        : `@${firstSegment.replace(/^@/u, '')}`;
  return {
    id: `${platform}:${normalized}`,
    platform,
    kind:
      platform === 'instagram' ? 'Instagram' : platform === 'tiktok' ? 'TikTok' : null,
    label,
    icon:
      platform === 'instagram' ? 'camera' : platform === 'tiktok' ? 'music' : 'globe',
    url: `https://${normalized}`,
    ownership: 'mine',
  };
}

/** True when the required Website is present; one owned social is optional. */
export function contextSourcesAreComplete(
  sources: readonly ContextSource[],
  options: ContextSourceOptions = {},
): boolean {
  const primary = sources[0];
  if (
    options.instagramBusinessEnabled &&
    sources.length === 1 &&
    primary?.platform === 'instagram' &&
    primary.ownership === 'mine'
  )
    return classifyInstagramBusinessUrl(primary.url) !== null;
  const websites = sources.filter(
    (source) => source.platform === 'website' && source.ownership === 'mine',
  );
  const socials = sources.filter(
    (source) =>
      (source.platform === 'instagram' || source.platform === 'tiktok') &&
      source.ownership === 'mine',
  );
  return (
    sources.length >= 1 &&
    sources.length <= 2 &&
    websites.length === 1 &&
    socials.length <= 1 &&
    websites.length + socials.length === sources.length &&
    socials.every((source) => contextValueIsValid(source.url, source.platform))
  );
}

/**
 * Inspect the shared composer value without depending on the selected guidance
 * control. Plain prose remains prose; URL-shaped values receive an explicit
 * invalid/unsupported result so the UI can preserve and explain them.
 */
export function inspectContextValue(
  raw: string,
  options: ContextSourceOptions = {},
): ContextValueInspection {
  const value = raw.trim();
  if (value.length === 0 || !intendsToBeUrl(value)) return { kind: 'text' };

  const scheme = value.match(/^([a-z][a-z0-9+.-]*):/iu)?.[1]?.toLowerCase();
  if (scheme !== undefined && scheme !== 'http' && scheme !== 'https') {
    return { kind: 'unsupported' };
  }

  const parsed = parsedContextUrl(value);
  if (parsed === null) return { kind: 'invalid' };
  const host = parsed.hostname.toLowerCase().replace(/^www\./u, '');
  if (options.instagramBusinessEnabled && hostMatches(host, 'instagram.com')) {
    const instagram = classifyInstagramBusinessUrl(value);
    if (instagram === null) return { kind: 'invalid' };
    if (instagram.kind !== 'PROFILE') {
      return {
        kind: 'source',
        source: {
          id: `instagram:${instagram.canonicalUrl}`,
          platform: 'instagram',
          kind: 'Instagram',
          icon: 'camera',
          label:
            instagram.kind === 'SHARE'
              ? 'Instagram'
              : `Instagram / ${instagram.shortcode}`,
          url: instagram.canonicalUrl,
          originalUrl: value,
          ownership: 'mine',
        },
      };
    }
    const profile = contextSourceFrom(value, 'instagram');
    return profile === null
      ? { kind: 'invalid' }
      : { kind: 'source', source: { ...profile, originalUrl: value } };
  }
  const supported = contextPlatformForValue(value);
  if (supported === null) {
    if (hostMatches(host, 'instagram.com') || hostMatches(host, 'tiktok.com')) {
      return { kind: 'invalid' };
    }
    return WEBSITE_SOCIAL_HOSTS.some((domain) => hostMatches(host, domain))
      ? { kind: 'unsupported' }
      : { kind: 'invalid' };
  }
  const source = contextSourceFrom(value, supported);
  return source === null ? { kind: 'invalid' } : { kind: 'source', source };
}

/** Backward-compatible auto-classifier used by small helpers and tests. */
export function classifyContextUrl(raw: string): ContextSource | null {
  const inspected = inspectContextValue(raw);
  return inspected.kind === 'source' ? inspected.source : null;
}

/** Prefill only from a website the merchant explicitly identified as theirs. */
export function storeUrlFrom(sources: readonly ContextSource[]): string {
  const ownedSite = sources.find(
    (source) => source.platform === 'website' && source.ownership !== 'similar',
  );
  return ownedSite?.label ?? '';
}
